<script setup>
import { ref, computed, nextTick, watch, onUnmounted } from 'vue';
import { useFlowieHandoff } from '@/composables/useFlowieHandoff'

const props = defineProps({
    messages: { type: Array, default: () => [] },
    isGenerating: { type: Boolean, default: false },
    executingActions: { type: Set, default: () => new Set() },
    completedActions: { type: Set, default: () => new Set() },
    failedActions: { type: Map, default: () => new Map() },
    initialMessage: { type: String, default: '' },
});

const emit = defineEmits(['send', 'execute-action']);

const messageInput = ref('');

const { flowieComposerVisible, closeFlowieComposer } = useFlowieHandoff(
    () => props.initialMessage,
    messageInput
);
const messagesContainer = ref(null);
const funMessageIndex = ref(0);
const funMessageInterval = ref(null);
const progressWidth = ref(0);
const progressInterval = ref(null);

const funMessages = [
    { icon: 'M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z', text: 'Scanning your contacts...' },
    { icon: 'M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5', text: 'Analyzing relationships...' },
    { icon: 'M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z', text: 'Finding hot leads...' },
    { icon: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z', text: 'Checking recent activity...' },
    { icon: 'M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z', text: 'Reviewing deal pipeline...' },
    { icon: 'M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z', text: 'Preparing insights...' },
    { icon: 'M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z', text: 'Almost there...' },
    { icon: 'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z', text: 'Building your report...' },
];

const currentFunMessage = computed(() => funMessages[funMessageIndex.value % funMessages.length]);

const startFunMessages = () => {
    funMessageIndex.value = Math.floor(Math.random() * funMessages.length);
    progressWidth.value = 0;

    funMessageInterval.value = setInterval(() => {
        funMessageIndex.value = (funMessageIndex.value + 1) % funMessages.length;
    }, 3500);

    let elapsed = 0;
    progressInterval.value = setInterval(() => {
        elapsed += 200;
        progressWidth.value = Math.min(90, 30 * Math.log10(elapsed / 500 + 1));
    }, 200);
};

const stopFunMessages = () => {
    if (funMessageInterval.value) {
        clearInterval(funMessageInterval.value);
        funMessageInterval.value = null;
    }
    if (progressInterval.value) {
        clearInterval(progressInterval.value);
        progressInterval.value = null;
    }
    progressWidth.value = 100;
};

watch(() => props.isGenerating, (val) => {
    if (val) startFunMessages();
    else stopFunMessages();
});

const sendMessage = () => {
    const text = messageInput.value.trim();
    if (!text || props.isGenerating) return;
    messageInput.value = '';
    emit('send', text);
};

const handleKeydown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
    }
};

watch(() => props.messages.length, () => {
    nextTick(() => {
        if (messagesContainer.value) {
            messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
        }
    });
});

onUnmounted(() => {
    stopFunMessages();
});

const renderMarkdown = (text) => {
    if (!text) return '';
    let html = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    // Headings
    html = html.replace(/^#### (.+)$/gm, '<h4 class="font-semibold text-sm text-gray-700 mt-3 mb-1">$1</h4>');
    html = html.replace(/^### (.+)$/gm, '<h3 class="font-semibold text-sm text-gray-800 mt-3 mb-1">$1</h3>');
    html = html.replace(/^## (.+)$/gm, '<h2 class="font-bold text-base text-gray-900 mt-2 mb-1">$1</h2>');

    // Bold & italic
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

    // Links - [text](url)
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="mf-link">$1</a>');

    // Tables
    html = html.replace(/^\|(.+)\|\s*\n\|[-| :]+\|\s*\n((?:\|.+\|\s*\n?)*)/gm, (match, headerRow, bodyRows) => {
        const headers = headerRow.split('|').map(h => h.trim()).filter(Boolean);
        const headerHtml = headers.map(h => `<th class="px-2 py-1 text-left text-xs font-semibold text-gray-600 border-b border-gray-300">${h}</th>`).join('');
        const rows = bodyRows.trim().split('\n').map(row => {
            const cells = row.replace(/^\||\|$/g, '').split('|').map(c => c.trim());
            return '<tr>' + cells.map(c => `<td class="px-2 py-1 text-xs text-gray-700 border-b border-gray-100">${c}</td>`).join('') + '</tr>';
        }).join('');
        return `<table class="w-full my-2 border-collapse"><thead><tr>${headerHtml}</tr></thead><tbody>${rows}</tbody></table>`;
    });

    // Lists
    html = html.replace(/^(\d+)\.\s+(.+)$/gm, '<li class="ml-4 list-decimal">$2</li>');
    html = html.replace(/^[-•]\s+(.+)$/gm, '<li class="ml-4 list-disc">$1</li>');

    html = html.replace(/((?:<li class="ml-4 list-disc">.+<\/li>\s*)+)/g, '<ul class="my-1 space-y-0.5">$1</ul>');
    html = html.replace(/((?:<li class="ml-4 list-decimal">.+<\/li>\s*)+)/g, '<ol class="my-1 space-y-0.5">$1</ol>');

    // Paragraphs
    html = html.replace(/\n\n/g, '</p><p class="mt-2">');
    html = html.replace(/\n/g, '<br>');

    html = '<p>' + html + '</p>';
    html = html.replace(/<p>\s*(<h[2-4])/g, '$1');
    html = html.replace(/(<\/h[2-4]>)\s*<\/p>/g, '$1');
    html = html.replace(/<p>\s*(<table)/g, '$1');
    html = html.replace(/(<\/table>)\s*<\/p>/g, '$1');
    html = html.replace(/<p>\s*(<ul|<ol)/g, '$1');
    html = html.replace(/(<\/ul>|<\/ol>)\s*<\/p>/g, '$1');
    html = html.replace(/<p>\s*<\/p>/g, '');

    return html;
};
</script>

<template>
    <div class="flex-1 flex flex-col overflow-hidden">
        <flowie-handoff-composer
            v-model="messageInput"
            :visible="flowieComposerVisible"
            :disabled="isGenerating"
            :placeholder="_mft('crm-tom:tom.placeholder')"
            @close="closeFlowieComposer"
            @send="sendMessage"
        />

        <!-- Messages -->
        <div ref="messagesContainer" class="flex-1 overflow-y-auto p-4 space-y-3">
            <!-- Empty state -->
            <div v-if="messages.length === 0 && !isGenerating" class="text-center text-gray-400 text-sm mt-8">
                <div class="flex items-center justify-center w-12 h-12 mx-auto mb-3 rounded-full bg-aiAgentRelationshipManager">
                    <ai-agent-relationship-manager-avatar class="w-full h-full" />
                </div>
                <p class="mb-1">
                    <mf-translate domainkey="crm-tom:tom.emptyState"></mf-translate>
                </p>
                <p class="text-xs">
                    <mf-translate domainkey="crm-tom:tom.emptyStateExample"></mf-translate>
                </p>
            </div>

            <!-- Message list -->
            <div
                v-for="(msg, index) in messages"
                :key="index"
                :class="msg.role === 'user' ? 'flex justify-end' : 'flex justify-start'"
            >
                <!-- User messages -->
                <div
                    v-if="msg.role === 'user'"
                    class="bg-aiAgentRelationshipManager text-gray-900 rounded-2xl rounded-br-md px-4 py-2 max-w-[85%] text-sm whitespace-pre-line"
                >
                    {{ msg.content }}
                </div>

                <!-- Assistant messages -->
                <div v-else class="max-w-[90%] space-y-2">
                    <div class="bg-gray-100 text-gray-800 rounded-2xl rounded-bl-md px-4 py-2 text-sm" v-html="renderMarkdown(msg.content)">
                    </div>

                    <utility-ai-usage-cta v-if="msg.budgetExceeded" class="ml-1" />

                    <!-- Lead results -->
                    <div v-if="msg.lead_results?.length" class="ml-1 mt-2 space-y-1">
                        <p class="text-xs text-gray-500 font-medium">{{ msg.lead_results.length }} <mf-translate domainkey="crm-tom:tom.leadsFound"></mf-translate></p>
                        <div v-for="lead in msg.lead_results" :key="lead.id" class="p-2 bg-white rounded border text-xs">
                            <a :href="'/crm/leads/' + lead.id" class="font-medium text-gray-800 mf-link">{{ lead.name }}</a>
                            <div class="text-gray-500">{{ lead.company }} · {{ lead.email }}</div>
                            <div v-if="lead.tags?.length" class="flex flex-wrap gap-1 mt-1">
                                <span v-for="tag in lead.tags" :key="tag.id" class="px-1.5 py-0.5 bg-aiAgentRelationshipManager/40 text-gray-800 rounded text-[10px]">{{ tag.name }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Pending action buttons -->
                    <div v-if="msg.pending_actions?.length" class="ml-1 mt-2 space-y-2">
                        <div v-for="action in msg.pending_actions" :key="action.action_id">
                            <!-- Already executed: green checkmark -->
                            <span v-if="completedActions.has(action.action_id)" class="text-xs text-green-600 flex items-center gap-1">
                                &#10003; {{ action.label }}
                            </span>
                            <!-- Failed: red error -->
                            <span v-else-if="failedActions.has(action.action_id)" class="text-xs text-red-600">
                                &#10007; {{ action.label }}: {{ failedActions.get(action.action_id) }}
                            </span>
                            <!-- Pending: clickable button -->
                            <component v-else
                                :is="action.severity === 'high' ? 'button-primary' : 'button-secondary'"
                                @click="$emit('execute-action', action.action_id)"
                                :disabled="executingActions.has(action.action_id)"
                                size="sm"
                            >
                                <template v-if="executingActions.has(action.action_id)">
                                    <mf-translate domainkey="crm-tom:tom.executing"></mf-translate>
                                </template>
                                <template v-else>{{ action.label }}</template>
                            </component>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Generating animation -->
            <div v-if="isGenerating" class="flex justify-start generating-appear">
                <div class="w-full max-w-[90%] rounded-2xl rounded-bl-md overflow-hidden shadow-lg border border-aiAgentRelationshipManager bg-gradient-to-br from-aiAgentRelationshipManager/80 via-white to-aiAgentRelationshipManager/30">
                    <div class="h-1 w-full bg-gray-200 overflow-hidden">
                        <div
                            class="h-full bg-aiAgentRelationshipManager transition-all duration-700 ease-out"
                            :style="{ width: progressWidth + '%' }"
                        ></div>
                    </div>
                    <div class="px-5 py-4">
                        <div class="flex items-center gap-3 mb-3">
                            <div class="generating-icon flex-shrink-0 w-8 h-8 rounded-lg bg-aiAgentRelationshipManager flex items-center justify-center" :key="funMessageIndex">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                    <path stroke-linecap="round" stroke-linejoin="round" :d="currentFunMessage.icon" />
                                </svg>
                            </div>
                            <span class="text-sm font-medium text-gray-700 generating-text" :key="'t' + funMessageIndex">{{ currentFunMessage.text }}</span>
                        </div>
                        <div class="space-y-2">
                            <div class="flex gap-2">
                                <div class="h-2 rounded-full bg-aiAgentRelationshipManager/60 skeleton-shimmer" style="width: 65%"></div>
                                <div class="h-2 rounded-full bg-aiAgentRelationshipManager/40 skeleton-shimmer" style="width: 25%; animation-delay: 0.3s"></div>
                            </div>
                            <div class="flex gap-2">
                                <div class="h-2 rounded-full bg-aiAgentRelationshipManager/40 skeleton-shimmer" style="width: 40%; animation-delay: 0.15s"></div>
                                <div class="h-2 rounded-full bg-aiAgentRelationshipManager/60 skeleton-shimmer" style="width: 50%; animation-delay: 0.45s"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Input area -->
        <div class="border-t border-gray-200 p-3">
            <div class="flex items-stretch space-x-2">
                <textarea
                    v-model="messageInput"
                    @keydown="handleKeydown"
                    :disabled="isGenerating"
                    :placeholder="_mft('crm-tom:tom.placeholder')"
                    rows="2"
                    class="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-aiAgentRelationshipManager focus:border-aiAgentRelationshipManager disabled:opacity-50"
                ></textarea>
                <button-base
                    @click="sendMessage"
                    style-type="primary"
                    :disabled="!messageInput.trim() || isGenerating"
                    size="sm"
                    class=" !px-2 !py-2"
                >
                    <template #icon>
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/>
                        </svg>
                    </template>
                </button-base>
            </div>
        </div>
    </div>
</template>

<style scoped>
@keyframes skeleton-shimmer {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 1; }
}
.skeleton-shimmer {
    animation: skeleton-shimmer 2s ease-in-out infinite;
}

@keyframes generating-appear {
    from { opacity: 0; transform: translateY(8px) scale(0.97); }
    to { opacity: 1; transform: translateY(0) scale(1); }
}
.generating-appear {
    animation: generating-appear 0.4s ease-out;
}

@keyframes icon-pop {
    0% { transform: scale(0) rotate(-20deg); }
    60% { transform: scale(1.15) rotate(5deg); }
    100% { transform: scale(1) rotate(0deg); }
}
.generating-icon {
    animation: icon-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes text-slide {
    from { opacity: 0; transform: translateX(-8px); }
    to { opacity: 1; transform: translateX(0); }
}
.generating-text {
    animation: text-slide 0.4s ease-out;
}
</style>
